var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ExecutionQueue } from "gather-video-common/dist/src/public/ExecutionQueue";
import { GatherEventEmitter } from "gather-common-including-video/dist/src/public/GatherEventEmitter";
import { equals } from "ramda";
import { Logger } from "../Logger";
import { LoggableMethodFactory } from "../log.decorator";
import { LocalTrack } from "./LocalTrack";
import { summarizeMediaStreamTrack } from "../utils";
import { metricsManager as globalsMetricsManager } from "../Globals";
import { ErrorContext } from "gather-common/dist/src/public/utils";
const LoggableMethod = LoggableMethodFactory("LocalDevice");
export var LocalDeviceEvent;
(function (LocalDeviceEvent) {
    LocalDeviceEvent["StreamChanged"] = "StreamChanged";
    LocalDeviceEvent["Loading"] = "Loading";
    LocalDeviceEvent["Error"] = "Error";
    LocalDeviceEvent["HardwareMuted"] = "HardwareMuted";
    LocalDeviceEvent["ExternallyEnded"] = "ExternallyEnded";
})(LocalDeviceEvent || (LocalDeviceEvent = {}));
export class LocalDevice extends GatherEventEmitter {
    audioTrack;
    videoTrack;
    settings;
    stopped = true;
    currentStreamSettings;
    getStreamWithErrors;
    initialSettings;
    queue = new ExecutionQueue();
    getConstraints;
    name;
    constructor(name, initialSettings, getStreamWithErrors, getConstraints) {
        super();
        this.initialSettings = { ...initialSettings };
        this.settings = { ...initialSettings };
        this.getStreamWithErrors = getStreamWithErrors;
        this.getConstraints = getConstraints;
        this.queue = new ExecutionQueue();
        this.queue.addIsExecutingChangeListener((executing) => {
            this.publishEvent(LocalDeviceEvent.Loading, executing);
        });
        this.name = name;
    }
    extendInitialSettings(settings) {
        this.initialSettings = {
            ...this.initialSettings,
            ...settings,
        };
    }
    start() {
        this.settings = { ...this.initialSettings };
        return () => {
            this.mute();
        };
    }
    async mute() {
        this.stopped = true;
        return await this.reconcileStreamState();
    }
    async unmute() {
        this.stopped = false;
        return await this.reconcileStreamState();
    }
    isMuted() {
        return this.stopped;
    }
    async changeSettings(settingsChange, reload = true) {
        this.settings = { ...this.settings, ...settingsChange };
        if (reload) {
            return await this.reconcileStreamState();
        }
        else {
            return;
        }
    }
    getCurrentConstraints() {
        if (!this.getConstraints)
            return;
        return this.getConstraints(this.currentStreamSettings ?? this.initialSettings);
    }
    async updateConstraints(kind, constraints) {
        return this.queue.run(async () => {
            const track = (kind === "audio" ? this.audioTrack : this.videoTrack)?.get();
            if (!track)
                return false;
            try {
                const currentConstraints = track.getConstraints();
                const newConstraints = {
                    ...currentConstraints,
                    ...constraints,
                };
                if (!equals(currentConstraints, newConstraints)) {
                    await track.applyConstraints(newConstraints);
                    Logger.debug(`Updated local ${this.name} ${kind} device constraints to: ${JSON.stringify(constraints)}`);
                }
            }
            catch (e) {
                Logger.error(`Failed applying constraint updates for kind`, new ErrorContext({ constraints: JSON.stringify(constraints), kind, localDeviceType: this.name }, e));
                return false;
            }
            return true;
        });
    }
    active() {
        return !!(this.audioTrack || this.videoTrack);
    }
    stopStream() {
        this.currentStreamSettings = undefined;
        this.audioTrack?.stop();
        this.audioTrack = undefined;
        this.videoTrack?.stop();
        this.videoTrack = undefined;
    }
    setupTrack(kind, trackResult) {
        if (trackResult.ok) {
            const track = trackResult.value;
            Logger.info("[LMM] setting up", kind, JSON.stringify(summarizeMediaStreamTrack(track)));
            if (kind === "audio") {
                this.audioTrack = new LocalTrack(track);
            }
            else {
                this.videoTrack = new LocalTrack(track);
            }
            track.addEventListener("ended", (e) => {
                Logger.warn("Media track ended", { kind, event: e });
                globalsMetricsManager.addMetricReliably("gvc-local-device-ended", {
                    kind,
                });
                this.stopStream();
                this.publishEvent(LocalDeviceEvent.StreamChanged);
                this.publishEvent(LocalDeviceEvent.ExternallyEnded);
            });
            track.addEventListener("mute", () => {
                if (kind === "video") {
                    Logger.warn("Media track muted", { kind });
                    globalsMetricsManager.addMetricReliably("gvc-local-device-muted", {
                        localDeviceName: this.name,
                        kind,
                    });
                }
                this.publishEvent(LocalDeviceEvent.HardwareMuted, kind, true);
            });
            track.addEventListener("unmute", () => {
                this.publishEvent(LocalDeviceEvent.HardwareMuted, kind, false);
            });
            if (track.muted) {
                this.publishEvent(LocalDeviceEvent.HardwareMuted, kind, true);
            }
        }
        else {
            const trackErr = trackResult.error;
            Logger.warn(`Error in LocalDevice - name: ${this.name} - kind: ${kind} - error: ${trackErr.name} - ${trackErr.message} `);
            globalsMetricsManager.addMetricReliably("gvc-local-device-error", {
                localDeviceName: this.name,
                kind: kind,
                error: trackErr.name,
                message: trackErr.message,
            });
            this.publishEvent(LocalDeviceEvent.Error, kind, trackErr);
        }
    }
    async reconcileStreamState() {
        return await this.queue.run(async () => {
            if (this.stopped) {
                if (this.active()) {
                    this.stopStream();
                    this.publishEvent(LocalDeviceEvent.StreamChanged);
                }
                return;
            }
            else {
                const shouldGetStream = !equals(this.settings, this.currentStreamSettings);
                if (!shouldGetStream)
                    return;
                this.stopStream();
                this.currentStreamSettings = this.settings;
                const { audio: audioTrackOrErr, video: videoTrackOrErr } = await this.getStreamWithErrors(this.settings);
                if (audioTrackOrErr) {
                    this.setupTrack("audio", audioTrackOrErr);
                }
                if (videoTrackOrErr) {
                    this.setupTrack("video", videoTrackOrErr);
                }
                if (!this.active()) {
                    this.stopStream();
                }
                this.publishEvent(LocalDeviceEvent.StreamChanged);
                return;
            }
        });
    }
    debugInfo() {
        return {
            name: this.name,
            active: this.active(),
            settings: this.settings,
            audioTrack: summarizeMediaStreamTrack(this.audioTrack?.get()),
            videoTrack: summarizeMediaStreamTrack(this.videoTrack?.get()),
        };
    }
}
__decorate([
    LoggableMethod
], LocalDevice.prototype, "mute", null);
__decorate([
    LoggableMethod
], LocalDevice.prototype, "unmute", null);
__decorate([
    LoggableMethod
], LocalDevice.prototype, "changeSettings", null);
__decorate([
    LoggableMethod
], LocalDevice.prototype, "getCurrentConstraints", null);
__decorate([
    LoggableMethod
], LocalDevice.prototype, "reconcileStreamState", null);
//# sourceMappingURL=LocalDevice.js.map